'use client';

/**
 * GoogleReviewsSmartRouting — the killer mechanic.
 *
 * 3-face filter: when a customer taps the Google badge on their
 * loyalty card, they pick a face (green / orange / red). The face
 * decides what happens next:
 *   green  → Google review page
 *   orange → email to owner (private negative)
 *   red    → SMS + email to owner (urgent)
 *
 * This is the page's main visual: the smart-routing illustration
 * sits beside three "what happens" cards, one per face.
 */

import React from 'react';
import { IconType } from 'react-icons';
import { FaRegFrown, FaRegMeh, FaRegSmile } from 'react-icons/fa';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import { useTranslation } from '@/i18n/client';

type Props = { lang: Locale };

type FaceKey = 'green' | 'orange' | 'red';
type FaceCopy = {
  Icon: IconType;
  label: string;
  outcome: string;
  body: string;
};

const FACE_ICON: Record<FaceKey, IconType> = {
  green: FaRegSmile,
  orange: FaRegMeh,
  red: FaRegFrown,
};

const FACE_TINT: Record<FaceKey, { bg: string; border: string; accent: string }> = {
  green: {
    bg: 'rgba(34,211,238,0.06)',
    border: 'rgba(34,211,238,0.30)',
    accent: '#22D3EE',
  },
  orange: {
    bg: 'rgba(255,183,77,0.06)',
    border: 'rgba(255,183,77,0.30)',
    accent: '#FFB74D',
  },
  red: {
    bg: 'rgba(255,77,77,0.06)',
    border: 'rgba(255,77,77,0.30)',
    accent: '#FF4D4D',
  },
};

const FACES: FaceKey[] = ['green', 'orange', 'red'];

const GoogleReviewsSmartRouting = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const copyFor = (k: FaceKey): FaceCopy => ({
    Icon: FACE_ICON[k],
    label: t(`GoogleReviews/SmartRouting/${k}/label`),
    outcome: t(`GoogleReviews/SmartRouting/${k}/outcome`),
    body: t(`GoogleReviews/SmartRouting/${k}/body`),
  });

  return (
    <section
      id="smart-routing"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0A0E27',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="01" label={t('GoogleReviews/SmartRouting/badge')} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {t('GoogleReviews/SmartRouting/title1')}{' '}
          <span style={{ color: '#B4C1D8' }}>{t('GoogleReviews/SmartRouting/title2')}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {t('GoogleReviews/SmartRouting/subtitle')}
        </p>

        <div
          className="grid gap-5 items-stretch"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}
        >
          {FACES.map((k) => {
            const c = copyFor(k);
            const tint = FACE_TINT[k];
            return (
              <article
                key={k}
                className="rounded-2xl p-6 h-full"
                style={{
                  background: tint.bg,
                  border: `1px solid ${tint.border}`,
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    aria-hidden="true"
                    className="inline-flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{
                      width: 40,
                      height: 40,
                      background: tint.bg,
                      border: `1px solid ${tint.border}`,
                    }}
                  >
                    <c.Icon style={{ width: 22, height: 22, color: tint.accent }} />
                  </div>
                  <div className="flex-1">
                    <div
                      className="mono"
                      style={{
                        fontSize: 11,
                        letterSpacing: 1.5,
                        color: tint.accent,
                        textTransform: 'uppercase',
                        marginBottom: 4,
                      }}
                    >
                      {c.label}
                    </div>
                    <h3
                      className="font-semibold"
                      style={{
                        fontSize: 18,
                        color: '#F5F7FA',
                        margin: '0 0 8px',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {c.outcome}
                    </h3>
                    <p
                      style={{
                        color: '#B4C1D8',
                        fontSize: 14,
                        lineHeight: 1.55,
                        margin: 0,
                      }}
                    >
                      {c.body}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <aside
          className="mt-6 rounded-2xl p-5 flex items-start gap-4"
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <HiOutlineInformationCircle
            aria-hidden="true"
            style={{ width: 22, height: 22, color: '#6B7593', flexShrink: 0, marginTop: 2 }}
          />
          <div className="flex-1">
            <div
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: 2,
                color: '#6B7593',
                textTransform: 'uppercase',
                marginBottom: 4,
              }}
            >
              {t('GoogleReviews/SmartRouting/note_label')}
            </div>
            <p
              style={{
                color: '#B4C1D8',
                fontSize: 14,
                lineHeight: 1.55,
                margin: 0,
              }}
            >
              {t('GoogleReviews/SmartRouting/note_body')}
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default GoogleReviewsSmartRouting;
